"use client";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GroupIcon from "@mui/icons-material/Group";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
    Alert,
    Box,
    Button,
    Card,
    Checkbox,
    FormControlLabel,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
} from "@mui/material";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import { useState } from "react";

import type { GuestBookingFormValues } from "@/booking/Form";
import { HouseRulesDialog } from "@/booking/HouseRulesDialog";
import { StoreConfirmDialog } from "@/booking/StoreConfirmDialog";
import type { SiblingStore, StoreData } from "@/server-fns/booking/query";
import { getSiblingStoresFn, saveBookingByGuestFn } from "@/server-fns/booking/serverFns";
import { isBookingNotAvailableError, isUnknownError } from "@/types/errors";
import type { ISO8601DateTime } from "@/types/iso8601";
import { computeCancellationFeeAmountCents } from "@/utils/cancellationFee";
import { formatStoreAddress } from "@/utils/emails/bookingEmailContent";
import { formatGuestBreakdown, payingGuests, totalGuestsFromForm } from "@/utils/partySize";
import { splitFullName } from "@/utils/splitFullName";

// The exact payload shape saveBookingByGuestFn expects (minus captchaToken).
// Built once here and either sent straight through (fee-inactive path) or
// carried forward to PaymentStep, which appends the stripe* fields once the
// card is saved.
export type GuestBookingPayload = {
    storeId: string;
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
    countAdults: number;
    countKids: number;
    countHighchairs: number;
    startTime: ISO8601DateTime;
    note: string;
    marketingConsent: boolean;
    marketingConsentCopyText: string;
    // Seating area picked on the booking page (TableGroup id, null = general seating). Left out
    // entirely when the store has no area choice, so the payload is unchanged for those stores.
    areaId?: number | null;
};

const buildBookingPayload = (
    values: GuestBookingFormValues,
    storeId: string,
    marketingConsent: boolean,
    marketingConsentCopyText: string,
): GuestBookingPayload => {
    const date = dayjs(values.date).format("YYYY-MM-DD");
    const startTime = `${date}T${values.time}:00` as ISO8601DateTime;
    const { firstName, lastName } = splitFullName(values.fullName);
    return {
        storeId,
        firstName,
        lastName,
        email: values.email,
        mobile: values.phoneNumber,
        countAdults: values.adults,
        countKids: values.kids,
        countHighchairs: values.highchairs || 0,
        startTime,
        note: values.note,
        marketingConsent,
        marketingConsentCopyText,
        ...(values.areaId !== undefined ? { areaId: values.areaId } : {}),
    };
};

type Props = {
    values: GuestBookingFormValues;
    storeId: string;
    storeData: StoreData;
    captchaToken: string;
    // When true, this step's submit only builds the payload and hands it to
    // PaymentStep — it does NOT create the booking. Set by Form.tsx from
    // storeData.bookingSettings?.cancellationFeeEnabled && storeData.stripeConnectStatus === "ACTIVE".
    requiresCardCollection: boolean;
    onBack: () => void;
    onProceedToPayment: (payload: GuestBookingPayload) => void;
    onSuccess: (isAutoConfirmed: boolean) => void;
};

export const BookingSummary = ({
    values,
    storeId,
    storeData,
    captchaToken,
    requiresCardCollection,
    onBack,
    onProceedToPayment,
    onSuccess,
}: Props) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [marketingConsent, setMarketingConsent] = useState(false);
    const [siblingStores, setSiblingStores] = useState<SiblingStore[]>([]);
    const [showStoreConfirm, setShowStoreConfirm] = useState(false);
    const [showHouseRules, setShowHouseRules] = useState(false);
    // Stays set after a failed submit so a retry doesn't make the guest agree again
    const [houseRulesAgreed, setHouseRulesAgreed] = useState(false);

    const guestCount = payingGuests({ countAdults: values.adults, countKids: values.kids });
    const feeType = storeData.bookingSettings?.cancellationFeeType ?? "PER_PERSON";
    const feeAmountCents = computeCancellationFeeAmountCents(
        feeType,
        storeData.bookingSettings?.cancellationFeeAmountCents ?? 0,
        guestCount,
    );
    const feeAmountDollars = (feeAmountCents / 100).toFixed(2);
    const freeCancellationUntilHours = storeData.bookingSettings?.freeCancellationUntilHours ?? 0;

    // Exact wording shown to the guest; persisted alongside the consent flag so the
    // record is auditable under the Spam Act 2003 (Cth). Keep render + payload in sync.
    const marketingConsentLabel = `Send me offers, events & updates from ${storeData.name}`;

    const handleFinalSubmit = async () => {
        const payload = buildBookingPayload(
            values,
            storeId,
            marketingConsent,
            marketingConsentLabel,
        );

        if (requiresCardCollection) {
            onProceedToPayment(payload);
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            const result = await saveBookingByGuestFn({
                data: {
                    payload,
                    captchaToken,
                },
            });

            if (isBookingNotAvailableError(result)) {
                const totalGuests = totalGuestsFromForm(values);
                setError(
                    `Sorry, the booking is not available at the selected time for ${totalGuests} guests. Please try another time.`,
                );
                return;
            }

            if (isUnknownError(result)) {
                setError(result?.message || `Apologies, the booking couldn’t be saved.`);
                return;
            }

            onSuccess(result.isAutoConfirmed);
        } catch (error) {
            Sentry.captureException(error);
            console.error(JSON.stringify(error));
            setError(`Apologies, the booking couldn’t be saved due to an error.`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const storeConfirmedStorageKey = `store_confirmed_${storeId}`;

    // Gate on the "is this the right location?" dialog: skip it (and the sibling-store lookup
    // entirely) once this store has already been confirmed in this browser. Otherwise look up
    // sibling stores on demand — only owners with other active stores ever see the dialog.
    const submitAfterHouseRules = async () => {
        try {
            if (localStorage.getItem(storeConfirmedStorageKey) === "1") {
                void handleFinalSubmit();
                return;
            }
        } catch {
            // localStorage unavailable (e.g. private browsing) — fall through to the lookup below.
        }

        let stores: SiblingStore[] = [];
        try {
            stores = await getSiblingStoresFn({ data: { storeId } });
        } catch (error) {
            Sentry.captureException(error);
        }

        if (stores.length === 0) {
            void handleFinalSubmit();
            return;
        }

        setSiblingStores(stores);
        setShowStoreConfirm(true);
    };

    const handleSubmitClick = () => {
        if (storeData.houseRules.length > 0 && !houseRulesAgreed) {
            setShowHouseRules(true);
            return;
        }
        void submitAfterHouseRules();
    };

    const handleAgreeToHouseRules = () => {
        setHouseRulesAgreed(true);
        setShowHouseRules(false);
        void submitAfterHouseRules();
    };

    const handleConfirmStore = () => {
        try {
            localStorage.setItem(storeConfirmedStorageKey, "1");
        } catch {
            // localStorage unavailable — the dialog will just show again next time, which is fine.
        }
        setShowStoreConfirm(false);
        void handleFinalSubmit();
    };

    return (
        <Box
            component={Card}
            sx={{
                p: { xs: 2, sm: 4 },
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                border: "none",
                position: "relative",
                bgcolor: "white",
            }}
        >
            <Button
                onClick={onBack}
                startIcon={<ArrowBackIcon />}
                sx={{ mb: 3, textTransform: "none", color: "text.secondary" }}
            >
                {"Back to form"}
            </Button>

            <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
                Booking Summary
            </Typography>

            <Stack spacing={3} sx={{ mb: 5 }}>
                {storeData.streetAddress && (
                    <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 1.5,
                                color: "primary.main",
                                opacity: 0.8,
                            }}
                        >
                            <LocationOnIcon />
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5 }}>
                                Restaurant
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {storeData.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {formatStoreAddress(storeData)}
                            </Typography>
                        </Box>
                    </Box>
                )}

                <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            color: "primary.main",
                            opacity: 0.8,
                        }}
                    >
                        <GroupIcon />
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5 }}>
                            Guests
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {formatGuestBreakdown({
                                countAdults: values.adults,
                                countKids: values.kids,
                                countHighchairs: values.highchairs,
                            })}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {totalGuestsFromForm(values)} guests in total
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            color: "primary.main",
                            opacity: 0.8,
                        }}
                    >
                        <EventAvailableIcon />
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5 }}>
                            Date & Time
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {values.date.format("dddd, MMMM D")} @ {values.time}
                        </Typography>
                        {values.areaName && (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {values.areaName}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            color: "primary.main",
                            opacity: 0.8,
                        }}
                    >
                        <ContactPhoneIcon />
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5 }}>
                            Contact Info
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {values.fullName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {values.phoneNumber} | {values.email}
                        </Typography>
                    </Box>
                </Box>

                {values.note && (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "grey.50",
                            border: "1px solid",
                            borderColor: "grey.100",
                        }}
                    >
                        <Typography variant="caption" sx={{ color: "text.secondary", mb: 1 }}>
                            Note for the restaurant
                        </Typography>
                        <Typography variant="body2">{values.note}</Typography>
                    </Box>
                )}

                {values.highchairs > 0 && (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "rgba(2, 136, 209, 0.08)",
                            color: "info.main",
                            display: "flex",
                            gap: 2,
                            alignItems: "center",
                            border: "1px solid",
                            borderColor: "rgba(2, 136, 209, 0.2)",
                        }}
                    >
                        <InfoOutlinedIcon fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {values.highchairs} highchair{values.highchairs > 1 ? "s" : ""} will be
                            prepared. Highchairs are counted separately from kids and are included
                            in the guest total above.
                        </Typography>
                    </Box>
                )}
            </Stack>

            {storeData.houseRules.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        House Rules
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                        Please review the restaurant&apos;s house rules before confirming your
                        booking.
                    </Typography>
                    <List disablePadding>
                        {storeData.houseRules.map((rule) => (
                            <ListItem key={rule.id} alignItems="flex-start" sx={{ px: 0, py: 1 }}>
                                <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                                    <InfoOutlinedIcon
                                        sx={{ color: "primary.main", fontSize: 20 }}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={rule.rule}
                                    primaryTypographyProps={{
                                        variant: "body2",
                                        fontWeight: 500,
                                        sx: { color: "#374151" },
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}

            {requiresCardCollection && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    This restaurant requires a card on file to protect against no-shows. A
                    cancellation fee of ${feeAmountDollars} applies if you cancel within{" "}
                    {freeCancellationUntilHours}h of your booking, or don&apos;t show up.
                    You&apos;ll be asked to add a card next — it won&apos;t be charged now.
                </Alert>
            )}

            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
                By confirming, you agree to our{" "}
                <Link href="/terms" target="_blank" rel="noopener">
                    Terms of Service
                </Link>{" "}
                and{" "}
                <Link href="/privacy" target="_blank" rel="noopener">
                    Privacy Policy
                </Link>
                .
            </Typography>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={marketingConsent}
                        onChange={(e) => setMarketingConsent(e.target.checked)}
                    />
                }
                label={
                    <Box>
                        <Typography variant="body1">{marketingConsentLabel}</Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Unsubscribe anytime
                        </Typography>
                    </Box>
                }
                sx={{ mb: 3, alignItems: "flex-start" }}
            />

            {error && (
                <Typography color="error" sx={{ mb: 3, fontWeight: 500 }}>
                    {error}
                </Typography>
            )}

            <Button
                variant="contained"
                fullWidth
                size="large"
                disabled={isSubmitting}
                onClick={handleSubmitClick}
            >
                {isSubmitting
                    ? "Sending Request..."
                    : requiresCardCollection
                      ? "Continue to Add Card"
                      : "Send Booking Request"}
            </Button>

            <HouseRulesDialog
                open={showHouseRules}
                houseRules={storeData.houseRules}
                onAgree={handleAgreeToHouseRules}
                onClose={() => setShowHouseRules(false)}
            />

            <StoreConfirmDialog
                open={showStoreConfirm}
                storeName={storeData.name}
                storeAddress={storeData}
                siblingStores={siblingStores}
                onConfirm={handleConfirmStore}
                onClose={() => setShowStoreConfirm(false)}
            />
        </Box>
    );
};
